import {
  getExperimentCookieName,
  parseExperimentOverridesFromSearch,
} from './experimentCookie';
import { isPersonalIp } from './personalIps';
import type {
  ExperimentDefinition,
  ResolveExperimentContext,
  ResolveExperimentResult,
} from './types';

function isVariantValid(
  experiment: ExperimentDefinition,
  variant: string,
): boolean {
  return variant in experiment.variants;
}

function matchesCountryTargeting(
  countries: readonly string[] | undefined,
  geoCountry: string | undefined,
): boolean {
  if (!countries?.length) return true;
  if (!geoCountry) return false;
  return countries.includes(geoCountry.toUpperCase());
}

function matchesLanguageTargeting(
  languages: readonly string[] | undefined,
  language: string | undefined,
): boolean {
  if (!languages?.length) return true;
  if (!language) return false;
  return languages.includes(language.toLowerCase());
}

function matchesPersonalIpTargeting(
  experiment: ExperimentDefinition,
  context: Pick<ResolveExperimentContext, 'clientIp' | 'getCookie'>,
): boolean {
  if (!experiment.targeting?.personalIpsOnly) return true;
  if (context.clientIp) return isPersonalIp(context.clientIp);
  return hasExperimentCookie(experiment, context);
}

function isInTargeting(
  experiment: ExperimentDefinition,
  context: Pick<
    ResolveExperimentContext,
    'clientIp' | 'geoCountry' | 'language' | 'getCookie'
  >,
): boolean {
  return (
    matchesCountryTargeting(
      experiment.targeting?.countries,
      context.geoCountry,
    ) &&
    matchesLanguageTargeting(
      experiment.targeting?.languages,
      context.language,
    ) &&
    matchesPersonalIpTargeting(experiment, context)
  );
}

function hasExperimentCookie(
  experiment: ExperimentDefinition,
  context: Pick<ResolveExperimentContext, 'getCookie'>,
): boolean {
  const cookieName = getExperimentCookieName(
    experiment.name,
    experiment.version,
  );
  return Boolean(context.getCookie(cookieName));
}

function pickVariantByRatio(
  experiment: ExperimentDefinition,
  random: () => number,
): string {
  let cumulative = 0;
  const roll = random();

  for (const [variant, { ratio }] of Object.entries(experiment.variants)) {
    cumulative += ratio;
    if (roll < cumulative) {
      return variant;
    }
  }

  return experiment.controlVariant;
}

export function resolveExperiment(
  experiment: ExperimentDefinition,
  context: ResolveExperimentContext,
): ResolveExperimentResult {
  const botVariant = experiment.botVariant ?? experiment.controlVariant;

  if (!experiment.enabled) {
    return {
      variant: experiment.controlVariant,
      setCookie: false,
      clearCookie: hasExperimentCookie(experiment, context),
    };
  }

  if (!isInTargeting(experiment, context)) {
    return {
      variant: experiment.controlVariant,
      setCookie: false,
      clearCookie: hasExperimentCookie(experiment, context),
    };
  }

  if (context.isBot) {
    return { variant: botVariant, setCookie: false, clearCookie: false };
  }

  const overrides = parseExperimentOverridesFromSearch(context.urlSearch);
  const urlOverride = overrides[experiment.name];

  if (urlOverride && isVariantValid(experiment, urlOverride)) {
    if (!context.isProduction || urlOverride === experiment.controlVariant) {
      return { variant: urlOverride, setCookie: true, clearCookie: false };
    }
  }

  const cookieName = getExperimentCookieName(
    experiment.name,
    experiment.version,
  );
  const cookieValue = context.getCookie(cookieName);

  if (cookieValue && isVariantValid(experiment, cookieValue)) {
    return { variant: cookieValue, setCookie: false, clearCookie: false };
  }

  const variant = pickVariantByRatio(experiment, context.random);
  return { variant, setCookie: true, clearCookie: false };
}

export function resolveExperimentFromOverride(
  experiment: ExperimentDefinition,
  overrideVariant: string | undefined,
  context: Pick<
    ResolveExperimentContext,
    | 'clientIp'
    | 'geoCountry'
    | 'language'
    | 'getCookie'
    | 'isBot'
    | 'isProduction'
  >,
): ResolveExperimentResult {
  if (!experiment.enabled) {
    return {
      variant: experiment.controlVariant,
      setCookie: false,
      clearCookie: false,
    };
  }

  if (!isInTargeting(experiment, context)) {
    return {
      variant: experiment.controlVariant,
      setCookie: false,
      clearCookie: false,
    };
  }

  if (context.isBot) {
    return {
      variant: experiment.botVariant ?? experiment.controlVariant,
      setCookie: false,
      clearCookie: false,
    };
  }

  if (overrideVariant && isVariantValid(experiment, overrideVariant)) {
    if (context.isProduction && overrideVariant !== experiment.controlVariant) {
      return {
        variant: experiment.controlVariant,
        setCookie: false,
        clearCookie: false,
      };
    }
    return { variant: overrideVariant, setCookie: true, clearCookie: false };
  }

  return {
    variant: experiment.controlVariant,
    setCookie: false,
    clearCookie: false,
  };
}
